import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const empty = { name: "", city: "", district: "", address: "", phone: "", website: "", brand_ids: [] };

export default function AdminDealers() {
  const [dealers, setDealers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () =>
    Promise.all([api.get("/dealers"), api.get("/brands")]).then(([d, b]) => {
      setDealers(d.data);
      setBrands(b.data);
    });
  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing("new");
    setForm(empty);
  };
  const openEdit = (d) => {
    setEditing(d.id);
    setForm({ ...empty, ...d });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") await api.post("/dealers", form);
      else await api.put(`/dealers/${editing}`, form);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.name}"?`)) return;
    await api.delete(`/dealers/${d.id}`);
    toast.success("Deleted");
    load();
  };

  const toggleBrand = (id) => {
    const exists = form.brand_ids.includes(id);
    setForm({
      ...form,
      brand_ids: exists ? form.brand_ids.filter((x) => x !== id) : [...form.brand_ids, id],
    });
  };

  return (
    <div data-testid="admin-dealers-page">
      <div className="flex justify-between items-center mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Network</div>
          <h1 className="font-display text-4xl tracking-tighter">Dealers</h1>
        </div>
        <button onClick={openCreate} className="brand-btn px-5 py-2.5 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2" data-testid="dealer-create-btn">
          <Plus size={14} /> New Dealer
        </button>
      </div>

      <div className="border border-white/10 bg-[#0A0A0A]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-left text-xs uppercase tracking-[0.2em] text-zinc-500">
              <th className="px-4 py-3 font-normal">Name</th>
              <th className="px-4 py-3 font-normal">City</th>
              <th className="px-4 py-3 font-normal">District</th>
              <th className="px-4 py-3 font-normal">Phone</th>
              <th className="px-4 py-3 font-normal">Brands</th>
              <th className="px-4 py-3 font-normal text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {dealers.map((d) => (
              <tr key={d.id} className="border-b border-white/5" data-testid={`dealer-row-${d.id}`}>
                <td className="px-4 py-3 font-medium">{d.name}</td>
                <td className="px-4 py-3 text-zinc-400">{d.city}</td>
                <td className="px-4 py-3 text-zinc-400">{d.district}</td>
                <td className="px-4 py-3 text-zinc-400">{d.phone}</td>
                <td className="px-4 py-3 text-zinc-400">{d.brand_ids?.map((id) => brands.find((b) => b.id === id)?.name).filter(Boolean).join(", ") || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openEdit(d)} className="text-zinc-400 hover:text-white p-2"><Edit2 size={14} /></button>
                  <button onClick={() => remove(d)} className="text-zinc-400 hover:text-red-400 p-2"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {dealers.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500">No dealers yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 sm:p-8" onClick={() => setEditing(null)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-[#0A0A0A] border border-white/10 p-6 my-8">
            <div className="flex justify-between items-center mb-6">
              <div className="font-display text-xl">{editing === "new" ? "New Dealer" : "Edit Dealer"}</div>
              <button type="button" onClick={() => setEditing(null)} className="text-zinc-400 hover:text-white"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="adm-input" data-testid="dealer-form-name" /></Field>
              <Field label="Phone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="adm-input" /></Field>
              <Field label="City"><input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="adm-input" data-testid="dealer-form-city" /></Field>
              <Field label="District"><input value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} className="adm-input" /></Field>
              <Field label="Website"><input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="adm-input" /></Field>
              <Field label="Address" full><textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="adm-input" /></Field>
              <Field label="Brands" full>
                <div className="flex flex-wrap gap-2">
                  {brands.map((b) => (
                    <button key={b.id} type="button" onClick={() => toggleBrand(b.id)} className={`px-3 py-1.5 text-xs border ${form.brand_ids.includes(b.id) ? "bg-white/10 border-white/40" : "border-white/10 hover:bg-white/5"}`}>
                      {b.name}
                    </button>
                  ))}
                </div>
              </Field>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setEditing(null)} className="px-5 py-2.5 text-xs uppercase tracking-[0.2em] border border-white/10 hover:bg-white/5">Cancel</button>
              <button type="submit" className="brand-btn px-5 py-2.5 text-xs uppercase tracking-[0.2em]" data-testid="dealer-form-save">Save</button>
            </div>
          </form>
        </div>
      )}
      <style>{`.adm-input { width: 100%; background: #141414; border: 1px solid rgba(255,255,255,0.1); padding: 0.625rem 0.875rem; font-size: 0.875rem; color: white; outline: none; } .adm-input:focus { border-color: rgba(255,255,255,0.3); }`}</style>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">{label}</label>
      {children}
    </div>
  );
}
